import { cn } from "design_system/libs"
import Title from "@love-irish/core-title"
import { addDynamicTags } from "./shared"
import { ResourceActions } from "./shared/resource_actions"
import Content from "@love-irish/core-content"
import TopicBadge from "@love-irish/resource-topic-badge"
import formatter from "design_system/utils/formatter"
import { format } from "timeago.js"
import React, { Fragment } from "react"
import { IconBook2, IconCode, IconList } from "@tabler/icons-react"

const labelComponents = {
  Collection: () => (
    <div className="flex gap-1 items-center text-xs mb-4">
      <IconList
        className="text-secondary-400 inline-block align-middle"
        size={14}
      />
      Collection
    </div>
  ),
  "Collection::Story": () => (
    <div className="flex gap-1 items-center text-xs mb-4">
      <IconBook2
        className="text-secondary-400 inline-block align-middle"
        size={14}
      />
      Story
    </div>
  ),
  "Item::Changelog": () => (
    <div className="flex gap-1 items-center text-xs mb-4">
      <IconCode
        className="text-secondary-400 inline-block align-middle"
        size={14}
      />
      Changelog
    </div>
  ),
}

const Basic = ({ item, className, currentUser, footer }: {
  item: Record<string, unknown>
  className?: string
  currentUser?: unknown
  /** Custom footer content to replace the default "Added by..." text */
  footer?: React.ReactNode
}) => {
  const typename = item.type || item.__typename || item.typename
  const LabelComponent = labelComponents[typename] || Fragment

  return (
    <div className={cn("border rounded-xl bg-background", className)}>
      {item.imageUrl && (
        <img
          src={item.imageUrl}
          className="w-full max-w-full rounded-t-xl overflow-hidden"
        />
      )}

      <div className="p-4">
        <LabelComponent />
        <div className="flex justify-between items-start mb-3 gap-8 mt-2">
          <Title
            className="post-title text-2xl flex gap-2"
            title={addDynamicTags(item.name || "Untitled")}
            size="sm"
            showIcon={false}
            href={item.path}
          />
          <div className="flex shrink-0 gap-1">
            <ResourceActions item={item} />
          </div>
        </div>

        {item.summary && (
          <div className="mt-4">
            <Content
              content={item.summary}
              className="prose-sm text-inherit text-sm text-dark dark:text-white font-mono! first-line:not-italic"
            />
          </div>
        )}

        {item.path && (
          <div className="mt-4">
            <a href={item.path}>
              <strong>Léigh mó</strong> &middot; <em>Read more</em> &rarr;
            </a>
          </div>
        )}

        {item.itemTopics?.length > 0 && (
          <div className="mt-6">
            {item.itemTopics.map((itemTopic) => (
              <TopicBadge topic={itemTopic.topic} key={itemTopic.id} />
            ))}
          </div>
        )}
      </div>
      <div className="p-4 border-t flex justify-between text-xs">
        {footer !== undefined ? (
          footer
        ) : (
          item.owner && (
            <span>
              Added by <strong>@{item.owner.slug}</strong> on{" "}
              <time
                title={new Date(item.createdAt as string).toLocaleString()}
                dateTime={item.createdAt as string}
                className="font-bold"
              >
                {formatter.shortDate(item.createdAt as string)}
              </time>
            </span>
          )
        )}
        <span>
          Updated{" "}
          <time
            title={new Date(item.updatedAt as string).toLocaleString()}
            dateTime={item.updatedAt as string}
            className="font-bold"
          >
            {format(item.updatedAt as string)}
          </time>
        </span>
      </div>
    </div>
  )
}

export default Basic
